'use client';

import { useContext, useEffect } from 'react';

import { AuthContext } from './context';
import { warmSessionCookie } from './warmSessionCookie';

/**
 * Reconciles the server `__session` cookie with the visitor's Firebase auth
 * state so the cookie is always PRESENT before an intercepted modal opens —
 * see {@link warmSessionCookie} for why that matters.
 *
 * Rendered INSIDE {@link AuthProvider}, it drives off that provider's
 * `currentUser$` rather than opening its own `onAuthStateChanged` subscription
 * — one Firebase load, one auth subscription, shared. `warmSessionCookie` runs
 * its reconciliation exactly ONCE per app lifecycle (module-scope guard), so even
 * though several `AuthProvider`s mount (each renders a `SessionWarmer`) and the
 * auth state changes during a session (e.g. the checkout engine minting a guest
 * in place), only the first definite auth state — at app start — reconciles the
 * cookie. Renders nothing.
 *
 * `keepGuestSession` is forwarded from the host {@link AuthProvider} — see
 * {@link warmSessionCookie} for the redirect-payment return it exists for.
 */
export function SessionWarmer({
  keepGuestSession = false,
  keepProfilelessSession = false,
}: {
  keepGuestSession?: boolean;
  keepProfilelessSession?: boolean;
}) {
  const context = useContext(AuthContext);
  const currentUser$ = context?.currentUser$;

  useEffect(() => {
    if (!currentUser$) return;
    const subscription = currentUser$.subscribe(user => {
      // Skip the initial unresolved emission; warm once Firebase reports a
      // definite signed-in user or a definite guest.
      if (user === undefined) return;
      void warmSessionCookie(user, {
        keepGuestSession,
        keepProfilelessSession,
      });
    });
    return () => subscription.unsubscribe();
  }, [currentUser$, keepGuestSession, keepProfilelessSession]);

  return null;
}
